/**
 * Tutors {@link DataTable} for the management page.
 * List + filters + expanded details; create / edit / sessions / reports from the table.
 */
import * as React from 'react';
import { User as UserIcon } from 'lucide-react';
import { DataTable } from '@/components/common/data-table';
import { DataTableFilters } from '@/components/common/data-table/DataTableFilters';
import { Button } from '@/components/forms/Button';
import { TutorRow } from './TutorRow';
import { TutorCard } from './TutorCard';
import { TutorFiltersContent } from './TutorFilters';
import { TutorDetails } from './renderTutorDetails';
import { ArchiveTutorModal } from './ArchiveTutorModal';
import { ReactivateTutorModal } from './ReactivateTutorModal';
import { useListTutorsRemoteScoped, useGetTutorRemoteFullState } from '@/hooks/useUsers';
import { useAuthStore } from '@/stores/authStore';
import { useDataTable } from '@/hooks/useDataTable';
import { useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import { cn } from '@/lib/utils';
import { getRoleLabelPlural } from '@/utils/role-label-utils';
import { type TutorRemoteTabView as TabView } from '@/components/tutors/tutor-configs';
import {
  DEFAULT_TUTOR_FILTERS,
  ALL_TUTOR_FILTER_GROUP_IDS,
  TUTOR_FILTER_GROUP_LABELS,
  type TutorFilters,
  type FilterGroupId,
} from './TutorFilters';
import {
  buildTutorArchiveTarget,
  buildTutorRemoteListFilters,
  hasTutorRemoteActiveFilters,
  resolveTutorExpandedUserId,
  type TutorArchiveTarget,
  type TutorDataTablePageActions,
} from './tutor-table-utils';
import type { TutorRecord, OnboardingFilters } from '@/types/remote/tutor.types';
import type { ViewMode } from '@/types/data-table.types';
import type { ElementWidth } from '@/types/interface-elements.types';

/** List filters sent to the tutor export endpoint. */
type TutorExportFilters = Omit<OnboardingFilters, 'companyId' | 'page' | 'pageSize'>;

/** Props for {@link TutorsDataTable}. */
interface Props {
  viewMode?: ViewMode;
  onViewModeChange?: (m: ViewMode) => void;
  expandedRowId?: string | null;
  onToggleRow?: (id: string) => void;
  onCloseDetails?: () => void;
  onEditTutor?: (id: string, record: TutorRecord) => void;
  onViewSessions?: (record: TutorRecord) => void;
  onViewReports?: (record: TutorRecord) => void;
  enableFilters?: boolean;
  activeTab: TabView;
  pageActions?: TutorDataTablePageActions;
  onEditSection?: (stepKey: string, record: TutorRecord) => void;
  showExport?: boolean;
  /** Same filters as the open list query (without page/pageSize). */
  onExportExcel?: (filters: TutorExportFilters) => void;
  showCreateButton?: boolean;
  onCreateTutor?: () => void;
  isSection?: boolean;
  layoutWidth?: ElementWidth;
  toolbarLeft?: React.ReactNode;
}

/** Tutors {@link DataTable} for the management page. */
export const TutorsDataTable: React.FC<Props> = ({
  viewMode: externalViewMode,
  onViewModeChange,
  expandedRowId,
  onToggleRow,
  onCloseDetails,
  onEditTutor,
  onViewSessions,
  onViewReports,
  enableFilters = true,
  activeTab,
  pageActions,
  onEditSection,
  showExport,
  onExportExcel,
  showCreateButton,
  onCreateTutor,
  isSection,
  layoutWidth,
  toolbarLeft,
}) => {
  const { user } = useAuthStore();
  const companyId = user?.companyId ?? '';

  const dt = useDataTable<TutorFilters, FilterGroupId>({
    tableName: 'tutors',
    defaultFilters: DEFAULT_TUTOR_FILTERS,
    allFilterGroupIds: ALL_TUTOR_FILTER_GROUP_IDS,
    externalViewMode,
    onExternalViewModeChange: onViewModeChange,
    externalExpandedRowId: expandedRowId,
    onCloseDetails,
    isSection,
    layoutWidth,
  });

  const apiFilters = React.useMemo(
    () => ({
      ...buildTutorRemoteListFilters(dt.filters, activeTab),
      page: dt.currentPage,
      pageSize: dt.itemsPerPage,
    }),
    [dt.filters, activeTab, dt.currentPage, dt.itemsPerPage],
  );

  const exportFilters = React.useMemo(
    (): TutorExportFilters => buildTutorRemoteListFilters(dt.filters, activeTab),
    [dt.filters, activeTab],
  );

  const { data: apiResponse, isLoading } = useListTutorsRemoteScoped(apiFilters);

  const data = apiResponse?.data ?? [];
  const totalItems = apiResponse?.total ?? 0;
  const totalPages = Math.ceil(totalItems / dt.itemsPerPage) || 1;
  const startIndex = (dt.currentPage - 1) * dt.itemsPerPage;
  const endIndex = Math.min(startIndex + dt.itemsPerPage, totalItems);

  // Reset to page 1 + clear tab-specific filters when tab changes.
  // Compare with previous value to skip initial mount (and React 18 StrictMode's double-invoke).
  const prevActiveTabRef = React.useRef(activeTab);
  React.useEffect(() => {
    const prev = prevActiveTabRef.current;
    prevActiveTabRef.current = activeTab;
    if (prev === activeTab) return;
    dt.setCurrentPage(1);
    dt.setFilters((f) => ({
      ...f,
      tutorSituation: undefined,
      userStatus: undefined,
      stepKeys: undefined,
    }));
  }, [activeTab]);

  const expandedUserId = resolveTutorExpandedUserId(expandedRowId, data);

  const {
    data: expandedFullState,
    isLoading: isLoadingExpandedTutor,
    isError: isExpandedTutorError,
    error: expandedTutorError,
  } = useGetTutorRemoteFullState(expandedUserId ?? '', companyId, {
    enabled: !!expandedUserId && !!expandedRowId,
  });
  useUnitGetNotFound({
    isError: isExpandedTutorError,
    error: expandedTutorError,
    catalogKey: 'tutorNotFound',
    onClose: onCloseDetails,
    entityId: expandedRowId,
  });

  const expandedRecord = expandedFullState?.record ?? null;

  const [archiveTarget, setArchiveTarget] = React.useState<TutorArchiveTarget | null>(null);
  const [reactivateUserId, setReactivateUserId] = React.useState<string | null>(null);

  function handleArchive(userId: string): void {
    const record = data.find((row) => row.userId === userId);
    setArchiveTarget(buildTutorArchiveTarget(record, userId));
  }

  function handleReactivate(userId: string): void {
    setReactivateUserId(userId);
  }

  function handleCloseArchive(): void {
    setArchiveTarget(null);
  }

  function handleCloseReactivate(): void {
    setReactivateUserId(null);
  }

  const hasActiveFilters = React.useMemo(
    () => hasTutorRemoteActiveFilters(dt.filters),
    [dt.filters],
  );

  const customFilterRender = React.useCallback(
    () => (
      <TutorFiltersContent
        filters={dt.filters}
        visibleFilterIds={dt.visibleFilterIds as FilterGroupId[]}
        onFilterChange={(patch) => {
          dt.setFilters((prev) => ({ ...prev, ...patch }));
          dt.setCurrentPage(1);
        }}
        activeTab={activeTab}
      />
    ),
    [dt.filters, dt.visibleFilterIds, dt.setFilters, dt.setCurrentPage, activeTab],
  );

  const filterGroups = React.useMemo(
    () => [
      {
        id: 'filters',
        label: 'Filtres',
        type: 'custom' as const,
        customRender: customFilterRender,
      },
    ],
    [customFilterRender],
  );

  return (
    <DataTable
      data={data}
      totalItems={totalItems}
      isLoading={isLoading}
      viewMode={dt.viewMode}
      onViewModeChange={dt.handleViewModeChange}
      currentPage={dt.currentPage}
      totalPages={totalPages}
      startIndex={startIndex}
      endIndex={endIndex}
      itemLabel="tuteurs"
      onPageChange={dt.setCurrentPage}
      hasActiveFilters={hasActiveFilters}
      containerRef={dt.containerRef}
      expandedColSpan={8}
      toolbarActions={
        enableFilters || showExport || showCreateButton ? (
          <>
            {enableFilters && (
              <Button
                preset="toolbar-filter"
                className={cn(dt.showFilters && 'section-action-button-active')}
                showBadge={hasActiveFilters}
                onClick={() => {
                  if (!dt.showFilters) dt.setShowConfigure(false);
                  dt.setShowFilters((prev) => !prev);
                }}
              />
            )}
            {showExport && onExportExcel && (
              <Button
                preset="toolbar-export"
                tooltipText="Exporter la liste des tuteurs en Excel"
                onClick={() => onExportExcel(exportFilters)}
              />
            )}
            {showCreateButton && onCreateTutor && (
              <Button
                preset="toolbar-create"
                tooltipText="Créer un tuteur"
                onClick={() => {
                  onCloseDetails?.();
                  onCreateTutor();
                }}
              />
            )}
          </>
        ) : undefined
      }
      filtersPanel={
        enableFilters ? (
          <DataTableFilters
            show={dt.showFilters}
            hasActiveFilters={hasActiveFilters}
            filterGroups={filterGroups}
            onClearFilters={() => {
              dt.clearFilters();
              dt.setCurrentPage(1);
            }}
            resultCount={totalItems}
            resultLabel="tuteur(s)"
            onToggleConfigure={
              dt.allowedFilterIds.length > 0 ? () => dt.setShowConfigure((v) => !v) : undefined
            }
            showConfigure={dt.showConfigure}
            configureContent={
              <div className="flex flex-wrap gap-3">
                {ALL_TUTOR_FILTER_GROUP_IDS.filter((id) => {
                  if (id === 'filter-registration-step' && activeTab !== 'pre_registered')
                    return false;
                  return dt.allowedFilterIds.includes(id);
                }).map((id) => {
                  const isOn = dt.filterVisibility[id] ?? true;
                  return (
                    <Button
                      key={id}
                      variant="custom"
                      size="sm"
                      onClick={() => dt.handleToggleFilterVisibility(id)}
                      disabled={dt.isSavingFilterVisibility}
                      className={cn(
                        dt.isSavingFilterVisibility && 'opacity-60 cursor-not-allowed',
                        isOn ? 'filter-option-primary--active' : 'filter-option-primary',
                      )}
                    >
                      {TUTOR_FILTER_GROUP_LABELS[id]}
                    </Button>
                  );
                })}
              </div>
            }
          />
        ) : undefined
      }
      renderTableHead={() => (
        <tr>
          <th>Nom</th>
          <th>Contact</th>
          <th>Progression</th>
          <th>Étapes</th>
          <th>Inscription</th>
          <th>Statut</th>
          <th className="w-20 text-center">Actions</th>
        </tr>
      )}
      renderTableRow={(record) => (
        <TutorRow
          record={record}
          onView={onToggleRow}
          onEdit={onEditTutor}
          onViewSessions={onViewSessions}
          onViewReports={onViewReports}
          pageActions={pageActions}
          activeTab={activeTab}
          onArchive={handleArchive}
          onReactivate={handleReactivate}
        />
      )}
      renderCard={(record) => (
        <TutorCard
          record={record}
          onView={onToggleRow}
          onEdit={onEditTutor}
          onViewSessions={onViewSessions}
          onViewReports={onViewReports}
          pageActions={pageActions}
          activeTab={activeTab}
          onArchive={handleArchive}
          onReactivate={handleReactivate}
        />
      )}
      expandedRowId={dt.expandedRowId}
      expandedRowRef={dt.expandedRowRef}
      expandedCollapseRef={dt.expandedCollapseRef}
      expandedItem={expandedRecord}
      isExpandedDetailLoading={isLoadingExpandedTutor}
      renderExpandedRow={(record) => (
        <TutorDetails
          record={record}
          fullState={expandedFullState}
          onEditSection={onEditSection ? (stepKey) => onEditSection(stepKey, record) : undefined}
        />
      )}
      renderDetailsContent={(record) => (
        <TutorDetails
          record={record}
          fullState={expandedFullState}
          onEditSection={onEditSection ? (stepKey) => onEditSection(stepKey, record) : undefined}
        />
      )}
      onCloseDetails={onCloseDetails}
      emptyIcon={UserIcon}
      emptyText="Aucun tuteur trouvé"
      isSection={isSection}
      modals={
        <>
          <ArchiveTutorModal
            open={!!archiveTarget}
            userId={archiveTarget?.userId ?? ''}
            companyId={companyId}
            tutorName={archiveTarget?.tutorName}
            isTutoringInProgress={archiveTarget?.isTutoringInProgress}
            onClose={handleCloseArchive}
          />
          <ReactivateTutorModal
            open={!!reactivateUserId}
            userId={reactivateUserId ?? ''}
            companyId={companyId}
            onClose={handleCloseReactivate}
          />
        </>
      }
      title={getRoleLabelPlural('tutor')}
      sidebarMenuId="menu-tutors"
      toolbarLeft={toolbarLeft}
    />
  );
};
